import DOMPurify from 'dompurify';

/**
 * ONE shared DOMPurify configuration for all email composer surfaces
 * (individual composer, batch composer, template editors, previews).
 *
 * Design intent: sanitize aggressively for security (no scripts, no
 * javascript: URLs, no event handlers) while preserving every piece of
 * valid email formatting — paragraphs, headings, bold/italic/underline,
 * text alignment, font-size, colour, links and lists — plus the
 * `{{placeholder}}` merge-token spans the composer uses internally.
 *
 * Inline `style` attributes are NOT stripped wholesale. Instead a single
 * `afterSanitizeAttributes` hook keeps only an allow-list of
 * email-client-safe CSS properties and clamps `font-size` to 1–50px.
 */

// Email-client-safe inline CSS properties. Anything else is dropped.
const ALLOWED_STYLE_PROPS = new Set([
  'font-size',
  'color',
  'background-color',
  'text-align',
  'font-weight',
  'font-style',
  'text-decoration',
  'text-decoration-line',
  'line-height',
  'font-family',
  'margin',
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
  'padding',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  'border',
  'border-top',
  'border-right',
  'border-bottom',
  'border-left',
  'border-color',
  'border-width',
  'border-style',
  'border-collapse',
  'border-spacing',
  'width',
  'height',
  'max-width',
  'min-width',
  'display',
  'vertical-align',
  'list-style',
  'list-style-type',
  'text-transform',
  'letter-spacing',
  'word-spacing',
  'white-space',
  'float',
  'clear',
]);

const EMAIL_ALLOWED_TAGS = [
  'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'b', 'i', 'u', 'strong', 'em', 's', 'strike', 'del',
  'blockquote', 'pre', 'code', 'br', 'hr',
  'ul', 'ol', 'li',
  'a', 'span', 'div',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
  'img', 'sup', 'sub',
];

const EMAIL_ALLOWED_ATTR = [
  'href', 'src', 'alt', 'title', 'target', 'rel',
  'style', 'class',
  'width', 'height', 'align', 'valign',
  'colspan', 'rowspan', 'border', 'cellpadding', 'cellspacing',
  // Merge-token spans used by the composer to protect {{placeholders}}
  'data-merge-token', 'data-variable',
];

/** Clamp an inline font-size declaration to the supported 1–50px range. */
function clampFontSizeValue(value: string): string {
  const m = value.match(/^([\d.]+)\s*px$/i);
  if (m) {
    const n = Math.min(50, Math.max(1, Math.round(parseFloat(m[1]))));
    return `${n}px`;
  }
  return value;
}

/** Filter a raw style attribute down to the allow-listed properties. */
function filterStyleAttribute(style: string): string {
  const kept: string[] = [];
  for (const chunk of style.split(';')) {
    const decl = chunk.trim();
    if (!decl) continue;
    const idx = decl.indexOf(':');
    if (idx < 0) continue;
    const prop = decl.slice(0, idx).trim().toLowerCase();
    if (!ALLOWED_STYLE_PROPS.has(prop)) continue;
    let val = decl.slice(idx + 1).trim();
    if (!val) continue;
    if (prop === 'font-size') val = clampFontSizeValue(val);
    kept.push(`${prop}: ${val}`);
  }
  return kept.join('; ');
}

let hooksInstalled = false;

/**
 * Install the shared hooks exactly once per page lifetime.
 * DOMPurify.addHook is global — every composer surface must funnel through
 * this module instead of registering its own hooks (prevents double-apply
 * and divergent behaviour).
 */
function ensureHooksInstalled(): void {
  if (hooksInstalled || typeof window === 'undefined') return;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    const el = node as unknown as HTMLElement;
    if (!el.getAttribute) return;
    if (el.tagName === 'A') {
      // F-9: force rel=noopener on blank-target links; block javascript:/data: hrefs
      if (el.getAttribute('target') === '_blank') {
        el.setAttribute('rel', 'noopener noreferrer');
      }
      const href = el.getAttribute('href') || '';
      if (/^\s*(javascript|data|vbscript):/i.test(href)) {
        el.removeAttribute('href');
      }
    }
    const style = el.getAttribute('style');
    if (style) {
      const filtered = filterStyleAttribute(style);
      if (filtered) el.setAttribute('style', filtered);
      else el.removeAttribute('style');
    }
  });
  hooksInstalled = true;
}

/**
 * Sanitize arbitrary HTML into email-safe HTML.
 * Preserves: paragraphs, headings, bold/italic/underline, alignment,
 * font-size (1–50px), colour, links, lists, tables, images and
 * `{{placeholder}}` merge-token spans.
 */
export function sanitizeEmailHtml(html: string): string {
  if (!html) return '';
  ensureHooksInstalled();
  const out = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: EMAIL_ALLOWED_TAGS,
    ALLOWED_ATTR: EMAIL_ALLOWED_ATTR,
    FORBID_TAGS: [
      'style', 'script', 'form', 'input', 'button', 'select', 'textarea',
      'iframe', 'object', 'embed', 'link', 'meta', 'base',
    ],
  });
  return typeof out === 'string' ? out : String(out);
}
